import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Plus, Trash2, RotateCcw, FileUp, AlertCircle, CheckCircle2, Eye, Info, ShieldAlert,
  Sparkles
} from 'lucide-react';
import { NOMS_ICONES_DASHBOARD } from '../../constants/iconesDashboard';
import './ModaleDashboardsSurMesure.css';

/**
 * Les dashboards sur mesure d'un compte : la liste, la fiche, et l'historique des versions.
 *
 * ## Pourquoi cet écran est ici, sur la fiche du compte
 *
 * Le projet a déjà tranché deux fois que les réglages d'un compte vivent sur sa ligne dans
 * Comptes — c'est là que se posent ses destinations et son identifiant d'interface Google Ads.
 * Un onglet d'Administration serait l'administration *de la plateforme*, réservée aux
 * administrateurs, alors qu'un dashboard sur mesure appartient à une agence ; et une entrée de
 * navigation propre ferait une quatrième adresse où chercher les réglages d'un compte.
 *
 * Une modale plutôt que le tiroir `kco-reglages` : celui-ci est une bande de trois cases, et un
 * formulaire qui porte un champ de quarante kilo-octets n'y tient pas.
 *
 * ## Le geste : enregistrer, puis regarder
 *
 * Il n'y a pas de prévisualisation d'un HTML **non enregistré**, et c'est un choix : un aperçu de
 * brouillon ne pourrait pas lire de données — la porte du serveur est bornée par une fiche qui
 * n'existerait pas encore — et montrerait un dashboard vide, ce qui ne prouve rien. Une fiche naît
 * **inactive et invisible des clients** : on l'enregistre, on l'ouvre pour de vrai, et on ne la
 * diffuse qu'ensuite.
 *
 * ⚠️ **Mais la distinction qui compte n'est pas *enregistré / non enregistré* : c'est *promu /
 * non promu*.** Ce commentaire disait « pas de prévisualisation d'un brouillon » et on en a
 * d'abord conclu qu'aucun aperçu n'était possible avant publication — c'est faux. Une version
 * enregistrée sans être désignée par `versionActuelleId` est un **candidat** : elle vit sur une
 * fiche réelle, lit donc de vraies données, et se prévisualise sans que le client voie quoi que
 * ce soit changer. C'est ce que fait le bouton « Prévisualiser » de l'onglet Versions, et c'est
 * la pierre d'angle de la génération assistée : plusieurs écritures proposées, une seule promue.
 *
 * ⚠️ Préfixe de classes `kdsm-`, et aucune classe finissant par `-table` : `App.css` porte une
 * règle générique `table[class*="-table"]` qui attrape par sous-chaîne.
 */

/** Les icônes proposées. Liste fermée : un champ libre laisserait la carte porter n'importe quoi. */
// ⚠️ Dérivée de la table de résolution : une icône proposée ici sans composant pour la rendre
// s'afficherait sous son nom en toutes lettres, ce qui est arrivé sur l'accueil.
const ICONES = NOMS_ICONES_DASHBOARD;

/** Une fiche vierge. `actif` à vrai, `visibleClient` à faux : construire d'abord, montrer ensuite. */
const FICHE_VIERGE = {
  nom: '',
  sousTitre: '',
  description: '',
  icone: 'LayoutDashboard',
  datePlancher: '',
  inclureAujourdhui: false,
  ordre: 0,
  actif: true,
  visibleClient: false,
  requetes: {},
  requetesDerivees: null,
  parametres: null
};

const joliJson = (v) => (v == null ? '' : JSON.stringify(v, null, 2));

/*
 * ⚠️ `onGenerer` est **facultatif**, et le bouton n'apparaît pas sans lui. La génération est
 * réservée à l'éditeur de Kokpit : c'est l'appelant qui le sait, et poser ici une lecture du
 * rôle ferait un second endroit où la règle vit — celui qu'on oublie de changer.
 */
const ModaleDashboardsSurMesure = ({ item, token, onFermer, onGenerer }) => {
  const libraryItemId = item.databaseId || item.id;

  const [fiches, setFiches] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  /** `null` = la liste ; un objet = la fiche en cours d'édition. */
  const [edition, setEdition] = useState(null);
  const [versions, setVersions] = useState(null);
  const [calculs, setCalculs] = useState([]);
  const [contrat, setContrat] = useState(null);

  const entetes = { Authorization: `Bearer ${token}` };
  const entetesJson = { ...entetes, 'Content-Type': 'application/json' };

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await fetch(`/api/dashboards-sur-mesure?libraryItemId=${encodeURIComponent(libraryItemId)}`, { headers: entetes });
      const c = await r.json();
      if (!r.ok || !c.success) throw new Error(c.error || 'Chargement impossible');
      setFiches(c.dashboards || []);
      setErreur(null);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setChargement(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryItemId, token]);

  useEffect(() => { charger(); }, [charger]);

  // Les calculs dérivés que le serveur sait exécuter, avec la description de leurs paramètres :
  // c'est la seule documentation de ce qu'on peut écrire dans `requetesDerivees`.
  useEffect(() => {
    fetch('/api/dashboards-sur-mesure/calculs', { headers: entetes })
      .then(r => r.json())
      .then(c => setCalculs(c.calculs || []))
      .catch(() => setCalculs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const ouvrirContrat = () => {
    if (contrat) { setContrat(null); return; }
    fetch('/api/dashboards-sur-mesure/contrat', { headers: entetes })
      .then(r => r.json())
      .then(c => setContrat(c.contrat || "Le contrat d'auteur est introuvable."))
      .catch(() => setContrat("Le contrat d'auteur n'a pas pu être chargé."));
  };

  const ouvrirFiche = async (fiche) => {
    // La liste ne porte ni les requêtes ni les dérivées — la projection les retire pour ne pas
    // traîner leur poids. Il faut donc relire la fiche entière avant de l'éditer.
    if (!fiche) { setEdition({ ...FICHE_VIERGE, html: '' }); setVersions(null); return; }
    try {
      const r = await fetch(`/api/dashboards-sur-mesure/${fiche.id}`, { headers: entetes });
      const c = await r.json();
      if (!r.ok || !c.success) throw new Error(c.error || 'Lecture impossible');
      const d = c.dashboard;
      setEdition({
        ...FICHE_VIERGE,
        ...d,
        datePlancher: d.datePlancher || '',
        sousTitre: d.sousTitre || '',
        description: d.description || '',
        icone: d.icone || 'LayoutDashboard',
        html: ''
      });
      const rv = await fetch(`/api/dashboards-sur-mesure/${fiche.id}/versions`, { headers: entetes });
      const cv = await rv.json();
      setVersions(cv.success ? { liste: cv.versions, courante: cv.versionActuelleId } : null);
    } catch (e) {
      setErreur(e.message);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="kdsm-voile"
      onClick={onFermer}
      role="presentation"
    >
      <div className="kdsm-modale" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Dashboards sur mesure">
        <header className="kdsm-entete">
          <div>
            <h2>Dashboards sur mesure</h2>
            <p>{item.name} · {item.type}</p>
          </div>
          <button type="button" className="kdsm-fermer" onClick={onFermer} aria-label="Fermer">
            <X size={18} />
          </button>
        </header>

        {erreur && (
          <div className="kdsm-alerte">
            <AlertCircle size={15} />
            <span>{erreur}</span>
          </div>
        )}

        {contrat !== null && (
          <pre className="kdsm-contrat">{contrat}</pre>
        )}

        <div className="kdsm-corps">
          {edition
            ? (
              <Formulaire
                valeur={edition}
                calculs={calculs}
                versions={versions}
                libraryItemId={libraryItemId}
                entetesJson={entetesJson}
                entetes={entetes}
                onAnnuler={() => { setEdition(null); setVersions(null); }}
                onEnregistre={async () => { setEdition(null); setVersions(null); await charger(); }}
                onErreur={setErreur}
              />
            )
            : (
              <Liste
                fiches={fiches}
                chargement={chargement}
                onOuvrir={ouvrirFiche}
                onGenerer={onGenerer}
                onSupprimer={async (id) => {
                  const r = await fetch(`/api/dashboards-sur-mesure/${id}`, { method: 'DELETE', headers: entetes });
                  if (!r.ok) {
                    const c = await r.json().catch(() => ({}));
                    setErreur(c.error || 'Suppression impossible');
                    return;
                  }
                  await charger();
                }}
              />
            )}
        </div>

        <footer className="kdsm-pied">
          <button type="button" className="kdsm-lien" onClick={ouvrirContrat}>
            <Info size={14} /> {contrat !== null ? "Masquer le contrat d'auteur" : "Ce que doit respecter un dashboard hébergé"}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

/* ------------------------------------------------------------------------------------------- *
 * La liste
 * ------------------------------------------------------------------------------------------- */

const Liste = ({ fiches, chargement, onOuvrir, onSupprimer, onGenerer }) => {
  const [aSupprimer, setASupprimer] = useState(null);

  if (chargement) return <p className="kdsm-vide">Chargement…</p>;

  return (
    <>
      <div className="kdsm-barre">
        <button type="button" className="kdsm-principal" onClick={() => onOuvrir(null)}>
          <Plus size={15} /> Nouveau dashboard
        </button>
        {onGenerer && (
          <button type="button" className="kdsm-generer" onClick={onGenerer}>
            <Sparkles size={15} /> Générer avec Claude
          </button>
        )}
      </div>

      {fiches.length === 0
        ? (
          <p className="kdsm-vide">
            Aucun dashboard sur mesure sur ce compte. Un dashboard est un fichier HTML autonome :
            soit construit sur claude.ai avec le connecteur Kokpit puis collé ici, soit
            {onGenerer ? ' généré ici même, en conversant.' : ' collé ici.'}
          </p>
        )
        : (
          <ul className="kdsm-liste">
            {fiches.map((f) => (
              <li key={f.id}>
                <div className="kdsm-liste-nom">
                  <strong>{f.nom}</strong>
                  {f.sousTitre && <span className="kdsm-sous">{f.sousTitre}</span>}
                  <span className="kdsm-meta">
                    {f.datePlancher ? `depuis le ${f.datePlancher}` : 'sans plancher'}
                    {f.inclureAujourdhui ? ' · journée en cours incluse' : ''}
                    {` · ordre ${f.ordre}`}
                  </span>
                  {/* Conséquence voulue de l'absence de clé étrangère : la fiche survit au compte,
                      et se repointe. Le dire ici évite une carte qui mène à un refus. */}
                  {f.typeDivergent && (
                    <span className="kdsm-divergent">
                      <AlertCircle size={13} /> Ce dashboard lit du {f.sourceType}, le compte est du {f.compte?.type}
                    </span>
                  )}
                </div>
                <div className="kdsm-liste-etats">
                  <span className={f.actif ? 'kdsm-pastille active' : 'kdsm-pastille'}>
                    {f.actif ? 'actif' : 'inactif'}
                  </span>
                  <span className={f.visibleClient ? 'kdsm-pastille active' : 'kdsm-pastille'}>
                    {f.visibleClient ? 'visible clients' : 'interne'}
                  </span>
                </div>
                <div className="kdsm-liste-actions">
                  <button type="button" className="kdsm-action" onClick={() => onOuvrir(f)}>Modifier</button>
                  {aSupprimer === f.id
                    ? (
                      <>
                        <button type="button" className="kdsm-action danger" onClick={() => { setASupprimer(null); onSupprimer(f.id); }}>
                          Confirmer
                        </button>
                        <button type="button" className="kdsm-action" onClick={() => setASupprimer(null)}>Annuler</button>
                      </>
                    )
                    : (
                      <button type="button" className="kdsm-action danger" onClick={() => setASupprimer(f.id)} title="Supprimer, avec toutes ses versions">
                        <Trash2 size={14} />
                      </button>
                    )}
                </div>
              </li>
            ))}
          </ul>
        )}
    </>
  );
};

/* ------------------------------------------------------------------------------------------- *
 * Le formulaire
 * ------------------------------------------------------------------------------------------- */

const Formulaire = ({
  valeur, calculs, versions, libraryItemId, entetes, entetesJson,
  onAnnuler, onEnregistre, onErreur
}) => {
  const [f, setF] = useState(valeur);
  const [jsonRequetes, setJsonRequetes] = useState(joliJson(valeur.requetes));
  const [jsonDerivees, setJsonDerivees] = useState(joliJson(valeur.requetesDerivees));
  const [jsonParametres, setJsonParametres] = useState(joliJson(valeur.parametres));
  const [html, setHtml] = useState('');
  const [note, setNote] = useState('');
  const [origine, setOrigine] = useState('COLLE');
  const [erreurs, setErreurs] = useState([]);
  /**
   * Ce que le contrat d'auteur reproche au HTML enregistré.
   *
   * ⚠️ **Affiché, jamais bloquant.** Un dashboard tourne déjà en production : refuser
   * rétroactivement un fichier qui marche serait absurde. Ces verdicts servent à voir ce que le
   * bac à sable rendra inerte — et c'est précisément ce qu'il refuse *en silence*.
   */
  const [verdictContrat, setVerdictContrat] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const fichier = useRef(null);

  const maj = (champ) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setF((p) => ({ ...p, [champ]: v }));
  };

  /** Analyse un champ JSON, ou rend l'erreur avec le nom du champ. */
  const lireJson = (texte, nom, defaut) => {
    const t = (texte || '').trim();
    if (!t) return { ok: true, valeur: defaut };
    try { return { ok: true, valeur: JSON.parse(t) }; }
    catch (e) { return { ok: false, erreur: `${nom} : JSON invalide (${e.message})` }; }
  };

  const enregistrer = async () => {
    const r1 = lireJson(jsonRequetes, 'Requêtes déclarées', {});
    const r2 = lireJson(jsonDerivees, 'Calculs dérivés', null);
    const r3 = lireJson(jsonParametres, 'Paramètres', null);
    const mauvais = [r1, r2, r3].filter((r) => !r.ok).map((r) => r.erreur);
    if (mauvais.length) { setErreurs(mauvais); return; }

    const charge = {
      ...f,
      ordre: Number(f.ordre) || 0,
      datePlancher: f.datePlancher || null,
      requetes: r1.valeur,
      requetesDerivees: r2.valeur,
      parametres: r3.valeur,
      ...(html.trim() ? { html, origine, note: note || null } : {})
    };
    // Le compte vient de la ligne, jamais du formulaire : c'est lui qui décide du type de source.
    if (!f.id) charge.libraryItemId = libraryItemId;

    setEnCours(true);
    setErreurs([]);
    try {
      const r = await fetch(f.id ? `/api/dashboards-sur-mesure/${f.id}` : '/api/dashboards-sur-mesure', {
        method: f.id ? 'PUT' : 'POST',
        headers: entetesJson,
        body: JSON.stringify(charge)
      });
      const c = await r.json().catch(() => ({}));
      setVerdictContrat(c.contrat || null);
      if (!r.ok || !c.success) {
        // Le serveur revalide tout : noms de champs inconnus avec leurs candidats proches,
        // plafond manquant, motif dangereux. C'est là que ça doit se voir, pas à la première
        // ouverture par un client.
        setErreurs(c.erreurs && c.erreurs.length ? c.erreurs : [c.error || 'Enregistrement impossible']);
        return;
      }
      await onEnregistre();
    } catch (e) {
      onErreur(e.message);
    } finally {
      setEnCours(false);
    }
  };

  const retablir = async (versionId) => {
    const r = await fetch(`/api/dashboards-sur-mesure/${f.id}/version-actuelle`, {
      method: 'PUT', headers: entetesJson, body: JSON.stringify({ versionId })
    });
    if (!r.ok) { onErreur('Rétablissement impossible'); return; }
    await onEnregistre();
  };

  const lireFichier = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const lecteur = new FileReader();
    lecteur.onload = () => { setHtml(String(lecteur.result || '')); setOrigine('FICHIER'); };
    lecteur.readAsText(file);
  };

  return (
    <div className="kdsm-form">
      {erreurs.length > 0 && (
        <div className="kdsm-alerte">
          <AlertCircle size={15} />
          <ul>{erreurs.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      {verdictContrat && (verdictContrat.refus.length > 0 || verdictContrat.avertissements.length > 0) && (
        <div className="kdsm-alerte kdsm-alerte-contrat">
          <ShieldAlert size={15} />
          <div>
            <strong>Ce que le bac à sable rendra inerte</strong>
            <ul>
              {verdictContrat.refus.map((r, i) => <li key={`r${i}`}>{r.message}</li>)}
              {verdictContrat.avertissements.map((a, i) => <li key={`a${i}`}>{a.message}</li>)}
            </ul>
          </div>
        </div>
      )}

      <fieldset>
        <legend>Identité</legend>
        <label>Nom
          <input type="text" value={f.nom} onChange={maj('nom')} placeholder="Fiche Client 360, usage" />
        </label>
        <label>Sous-titre
          <input type="text" value={f.sousTitre} onChange={maj('sousTitre')} placeholder="Comment les équipes utilisent la fiche client" />
        </label>
        <label>Texte de la carte
          <input type="text" value={f.description} onChange={maj('description')} placeholder="Ce que la carte annonce dans l'écran de choix" />
        </label>
        <div className="kdsm-duo">
          <label>Icône
            <select value={f.icone} onChange={maj('icone')}>
              {ICONES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </label>
          <label>Ordre
            <input type="number" value={f.ordre} onChange={maj('ordre')} />
          </label>
        </div>
      </fieldset>

      <fieldset>
        <legend>Période</legend>
        <p className="kdsm-aide">
          C'est le sélecteur de dates de Kokpit qui pilote ce dashboard. Ces deux réglages bornent
          ce qu'il peut demander, et le serveur le dit à l'écran quand il ramène une période.
        </p>
        <div className="kdsm-duo">
          <label>Date plancher de reporting
            <input type="date" value={f.datePlancher} onChange={maj('datePlancher')} />
          </label>
          <label className="kdsm-case">
            <input type="checkbox" checked={f.inclureAujourdhui} onChange={maj('inclureAujourdhui')} />
            <span>Inclure la journée en cours (partielle)</span>
          </label>
        </div>
        <p className="kdsm-aide">
          À laisser décoché pour Matomo : l'archivage est différé, et les rapports détaillés
          s'arrêtent souvent en milieu de matinée.
        </p>
      </fieldset>

      <fieldset>
        <legend>Diffusion</legend>
        <label className="kdsm-case">
          <input type="checkbox" checked={f.actif} onChange={maj('actif')} />
          <span>Actif</span>
        </label>
        <label className="kdsm-case">
          <input type="checkbox" checked={f.visibleClient} onChange={maj('visibleClient')} />
          <span>Visible par les accès clients</span>
        </label>
        <p className="kdsm-aide">
          Il faut les deux, plus le droit « Dashboards sur mesure » accordé au client depuis
          l'onglet Accès clients de l'organisation.
        </p>
      </fieldset>

      <fieldset>
        <legend>Requêtes déclarées</legend>
        <p className="kdsm-aide">
          Les seules requêtes que ce dashboard pourra poser. Rien d'autre ne passera : le code
          d'une page hébergée tourne dans le navigateur de son lecteur.
          <br />
          <code>{'{ "clé": { "dimensions": [], "metrics": [], "limit": 200, "filters": [], "serveurSeulement": false } }'}</code>
          <br />
          Un <code>limit</code> est obligatoire dès qu'aucune dimension n'est temporelle : sans
          plafond déclaré, la source n'en rend que cinquante et les totaux seraient faux.
        </p>
        <textarea rows={10} value={jsonRequetes} onChange={(e) => setJsonRequetes(e.target.value)} spellCheck={false} />
      </fieldset>

      <fieldset>
        <legend>Calculs dérivés</legend>
        <p className="kdsm-aide">
          Pour tout comptage tiré de valeurs qui ne doivent pas atteindre le navigateur — URL,
          numéros, identifiants de compte. Le serveur lit les lignes et ne renvoie que des nombres.
          <br />
          <code>{'{ "clé": { "calcul": "...", "base": "clé d\'une requête", "parametres": {} } }'}</code>
        </p>
        {calculs.map((c) => (
          <details key={c.nom} className="kdsm-calcul">
            <summary><code>{c.nom}</code> — {c.description}</summary>
            <dl>
              {Object.entries(c.parametres || {}).map(([k, v]) => (
                <React.Fragment key={k}><dt><code>{k}</code></dt><dd>{v}</dd></React.Fragment>
              ))}
            </dl>
          </details>
        ))}
        <textarea rows={8} value={jsonDerivees} onChange={(e) => setJsonDerivees(e.target.value)} spellCheck={false} />
      </fieldset>

      <fieldset>
        <legend>Paramètres du dashboard</legend>
        <p className="kdsm-aide">
          Transmis à la page, et c'est ce qui permet au même fichier de servir deux clients :
          libellés, motifs de classement, intitulés de sections.
        </p>
        <textarea rows={5} value={jsonParametres} onChange={(e) => setJsonParametres(e.target.value)} spellCheck={false} />
      </fieldset>

      <fieldset>
        <legend>Le fichier HTML</legend>
        <p className="kdsm-aide">
          Collez le code depuis claude.ai, ou chargez le fichier téléchargé. Laissé vide, le HTML
          actuel est conservé — enregistrer la fiche ne crée pas de version.
        </p>
        <div className="kdsm-barre">
          <button type="button" className="kdsm-action" onClick={() => fichier.current?.click()}>
            <FileUp size={14} /> Charger un fichier
          </button>
          <input ref={fichier} type="file" accept=".html,text/html" onChange={lireFichier} style={{ display: 'none' }} />
          {html && <span className="kdsm-meta">{html.length.toLocaleString('fr-FR')} caractères prêts à enregistrer</span>}
        </div>
        <textarea
          rows={6} value={html} onChange={(e) => { setHtml(e.target.value); setOrigine('COLLE'); }}
          spellCheck={false} placeholder="<!doctype html> …"
        />
        <label>Note de version
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ce qui change dans cette version" />
        </label>
      </fieldset>

      {versions && versions.liste.length > 0 && (
        <fieldset>
          <legend>Versions</legend>
          <ul className="kdsm-versions">
            {versions.liste.map((v) => (
              <li key={v.id}>
                <span className="kdsm-meta">
                  {new Date(v.createdAt).toLocaleString('fr-FR')} · {v.origine} · {(v.octets / 1024).toFixed(1)} Ko
                  {/* `modele` et `coutEstime` étaient projetés par la route sans être affichés
                      nulle part : une version écrite par Claude ne disait ni par quel modèle, ni
                      à quel prix. */}
                  {v.modele ? ` · ${v.modele}` : ''}
                  {v.coutEstime ? ` · ${v.coutEstime.toFixed(3)} $` : ''}
                  {v.note ? ` · ${v.note}` : ''}
                </span>
                <div className="kdsm-liste-actions">
                  {/* Prévisualiser une version non promue : elle vit sur une fiche réelle, donc
                      elle lit de vraies données — c'est la seule façon de juger une écriture
                      avant de la publier. */}
                  <a
                    className="kdsm-action"
                    href={`#dashboard-sur-mesure?dashboard=${f.id}&version=${v.id}`}
                    onClick={onAnnuler}
                    title="Voir cette version avec de vraies données, sans la publier"
                  >
                    <Eye size={13} /> Prévisualiser
                  </a>
                  {v.id === versions.courante
                    ? <span className="kdsm-pastille active"><CheckCircle2 size={12} /> servie</span>
                    : (
                      <button type="button" className="kdsm-action" onClick={() => retablir(v.id)}>
                        <RotateCcw size={13} /> Rétablir
                      </button>
                    )}
                </div>
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      <div className="kdsm-barre kdsm-barre-fin">
        <button type="button" className="kdsm-action" onClick={onAnnuler}>Annuler</button>
        <button type="button" className="kdsm-principal" onClick={enregistrer} disabled={enCours}>
          {enCours ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        {f.id && (
          <a
            className="kdsm-lien"
            href={`#dashboard-sur-mesure?dashboard=${f.id}`}
            onClick={onAnnuler}
          >
            <Eye size={14} /> Ouvrir le dashboard
          </a>
        )}
      </div>
    </div>
  );
};

export default ModaleDashboardsSurMesure;
